import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { MODULE_META, BOTTOM_PRIORITY } from "@/config/modules";

const NavItem = ({ m, onClick }) => {
  const Icon = MODULE_META[m.id]?.icon;
  return (
    <NavLink
      to={`/${m.id}`}
      onClick={onClick}
      data-testid={`nav-${m.id}`}
      className={({ isActive }) =>
        `flex items-center gap-3 h-12 px-4 rounded-xl font-medium transition-colors ${
          isActive ? "bg-[#1E4D2B] text-white" : "text-[#1B2A26] hover:bg-[#E8F5E9]"
        }`
      }
    >
      {Icon && <Icon className="h-5 w-5 shrink-0" />}
      <span className="flex-1 truncate">{m.label}</span>
      {m.status === "soon" && <span className="text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-[#C86D51]/15 text-[#B05A40]">Em breve</span>}
    </NavLink>
  );
};

const UserCard = ({ onLogout }) => {
  const { user } = useAuth();
  return (
    <div className="border-t border-[#E2DDD3] pt-4 mt-4">
      <div className="flex items-center gap-3 px-2" data-testid="user-card">
        <div className="h-10 w-10 rounded-full bg-[#C86D51] text-white flex items-center justify-center font-bold overflow-hidden">
          {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : user.name[0]}
        </div>
        <div className="min-w-0">
          <p className="font-semibold truncate" data-testid="user-name">{user.name}</p>
          <p className="text-xs text-[#5C6B66]" data-testid="user-role">{user.role_label}</p>
        </div>
      </div>
      <button onClick={onLogout} data-testid="logout-button" className="mt-3 w-full h-12 rounded-xl flex items-center gap-3 px-4 text-[#B05A40] font-semibold hover:bg-[#C86D51]/10 transition-colors">
        <LogOut className="h-5 w-5" /> Sair
      </button>
    </div>
  );
};

const NavList = ({ onNavigate, onLogout }) => {
  const { modules } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <nav className="flex-1 space-y-1 overflow-y-auto">{modules.map((m) => <NavItem key={m.id} m={m} onClick={onNavigate} />)}</nav>
      <UserCard onLogout={onLogout} />
    </div>
  );
};

const BottomNav = ({ onMenu }) => {
  const { modules } = useAuth();
  const items = BOTTOM_PRIORITY.map((id) => modules.find((m) => m.id === id)).filter(Boolean).slice(0, 4);
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#E2DDD3] pb-safe" data-testid="bottom-nav">
      <div className="grid grid-cols-5">
        {items.map((m) => {
          const Icon = MODULE_META[m.id].icon;
          return (
            <NavLink key={m.id} to={`/${m.id}`} data-testid={`bottom-nav-${m.id}`}
              className={({ isActive }) => `h-16 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold ${isActive ? "text-[#1E4D2B]" : "text-[#5C6B66]"}`}>
              {({ isActive }) => (<>
                <span className={`h-8 w-12 rounded-full flex items-center justify-center transition-colors ${isActive ? "bg-[#E8F5E9]" : ""}`}><Icon className="h-5 w-5" /></span>
                <span className="truncate max-w-[72px]">{m.label.replace("Ordens de Serviço", "OS")}</span>
              </>)}
            </NavLink>
          );
        })}
        <button onClick={onMenu} data-testid="bottom-nav-menu" className="h-16 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold text-[#5C6B66]">
          <span className="h-8 w-12 rounded-full flex items-center justify-center"><Menu className="h-5 w-5" /></span>Menu
        </button>
      </div>
    </nav>
  );
};

export const AppShell = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { setOpen(false); await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#F4F1EA]">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-72 flex-col bg-white border-r border-[#E2DDD3] p-5 z-30" data-testid="sidebar">
        <div className="mb-6 px-1"><Logo /></div>
        <NavList onLogout={doLogout} />
      </aside>
      <header className="md:hidden sticky top-0 z-30 bg-[#F4F1EA]/90 backdrop-blur-xl border-b border-[#E2DDD3] px-4 h-16 flex items-center justify-between" data-testid="mobile-header">
        <Logo />
        <button onClick={() => setOpen(true)} className="h-12 w-12 rounded-xl bg-white border border-[#E2DDD3] flex items-center justify-center" data-testid="mobile-menu-button" aria-label="Abrir menu">
          <Menu className="h-6 w-6" />
        </button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-sm p-5 bg-white flex flex-col" data-testid="mobile-drawer">
          <SheetTitle className="mb-4"><Logo /></SheetTitle>
          <NavList onNavigate={() => setOpen(false)} onLogout={doLogout} />
        </SheetContent>
      </Sheet>
      <main className="md:pl-72 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10"><Outlet /></div>
      </main>
      <BottomNav onMenu={() => setOpen(true)} />
    </div>
  );
};
